import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  Stack,
  styled,
} from '@mui/material';
import { debounce, find, forEach, get, has, isNil, isPlainObject, omit } from 'lodash';

import LoadableMonacoEditor from '../elements/editors/monaco/LoadableMonacoEditor';
import {
  baseSelectStyles,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../elements/rselect/ReactSelectUtils';
import DebouncedText from '../elements/editors/mui/DebouncedText';
import UnsortedSelect from '../elements/rselect/UnsortedSelect';
import Constants from '../../Constants';
import JSONUtils from '../../lib/JSONUtils';
import { convertJSONtoScala, convertScalaToJson } from 'js/lib/Scala';

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: {
    fontSize: 12,
    fontWeight: 500,
    color: '#1F2F40',
    lineHeight: '24px',
    textTransform: 'initial',
    letterSpacing: '.5px',
  },
});

const StyledCheckbox = styled(Checkbox)({
  color: '#4AD496 !important',
  [`.${checkboxClasses.checked}`]: {
    color: '#4AD496 !important',
  },
});
const checkStyle = {};
const selectStyles = {
  ...baseSelectStyles,
  container: (styles) => ({ ...styles, minWidth: 80 }),
};

// Component Defination

const BatchFields = (props) => {
  // Constants
  const KEY_JSON_BODY = 'ui.body.transform.function';
  const KEY_FIELDNAME = 'ui.batch_fieldname';

  // State Defination
  const [groupingAlgo, updateGrouingAlgo] = useState(Constants.GROUPING_ALGO[0]);

  // Variables

  const batchMode =
    props.sinkConfig && !isNil(props.sinkConfig['batch.mode'])
      ? props.sinkConfig['batch.mode']
      : false;

  const batchSize =
    props.sinkConfig && !isNil(props.sinkConfig['batch.size'])
      ? props.sinkConfig['batch.size']
      : 100;

  const bodyTransFn =
    props.sinkConfig && !isNil(props.sinkConfig['body.transform.function'])
      ? props.sinkConfig['body.transform.function']
      : 'messages';

  const fieldName =
    props.sinkConfig && !isNil(props.sinkConfig[KEY_FIELDNAME]) && !!props.sinkConfig[KEY_FIELDNAME]
      ? get(props.sinkConfig, KEY_FIELDNAME, 'data')
      : 'data';

  const jsonParams =
    props.sinkConfig && !isNil(props.sinkConfig[KEY_JSON_BODY])
      ? props.sinkConfig[KEY_JSON_BODY]
      : '{}';

  const omitUiKeys = [KEY_FIELDNAME, KEY_JSON_BODY];

  // ComponentWillMount
  useEffect(() => {
    if (has(props, 'sinkConfig') && has(props.sinkConfig, 'body.transform.function')) {
      let tempGroupingAlgo;
      let payloadFieldName = 'data';

      const contentType =
        (props.sinkConfig && props.sinkConfig['content.type']) || 'application/json';
      let jsonParams = null;

      if (has(props.sinkConfig, 'ui.batch_algo') && !!props.sinkConfig['ui.batch_algo']) {
        tempGroupingAlgo = find(
          Constants.GROUPING_ALGO,
          (algo) => algo.value === props.sinkConfig['ui.batch_algo'],
        );
      }
      if (has(props.sinkConfig, KEY_JSON_BODY)) {
        jsonParams = props.sinkConfig[KEY_JSON_BODY];
      } else if (!tempGroupingAlgo) {
        // Fallback code, for object only..
        let bodyTransVal = props.sinkConfig['body.transform.function'];
        if (bodyTransVal.indexOf('Map') !== -1) {
          // Algo Object, convert scala format code to json
          jsonParams = convertScalaToJson(bodyTransVal);
          tempGroupingAlgo = Constants.GROUPING_ALGO[1];
        }
      }

      if (!tempGroupingAlgo && props.sinkConfig['body.transform.function'] === 'messages') {
        // Array algo
        tempGroupingAlgo = Constants.GROUPING_ALGO[0];
        jsonParams = '';
        payloadFieldName = 'data';
      }

      if (!tempGroupingAlgo) {
        tempGroupingAlgo = props.isEditMode
          ? Constants.GROUPING_ALGO[2]
          : Constants.GROUPING_ALGO[0];
      }

      // Grouping_ALGO[1] is Property inside JSON
      if (
        tempGroupingAlgo.value === Constants.GROUPING_ALGO[1].value &&
        jsonParams.length > 0 &&
        JSONUtils.isJSON(jsonParams)
      ) {
        jsonParams = JSONUtils.parseJson(jsonParams);
        forEach(jsonParams, function (value, key) {
          if (value === 'messages') {
            payloadFieldName = key;
            delete jsonParams[key];
            return false;
          }
        });
      }

      // If valid json object, then stringify otherwise keep the original value.
      jsonParams = isPlainObject(jsonParams)
        ? JSONUtils.parseToFormat(jsonParams, contentType)
        : jsonParams;

      // Updating Algo Value in state
      updateGrouingAlgo(tempGroupingAlgo);

      let newConfig = {
        'ui.batch_algo': has(props.sinkConfig, 'ui.batch_algo')
          ? props.sinkConfig['ui.batch_algo']
          : tempGroupingAlgo.value,
      };

      if (tempGroupingAlgo.value === Constants.GROUPING_ALGO[1].value) {
        // Property Inside json algo, then only update FieldName and JSON body
        if (
          (!has(props.sinkConfig, KEY_JSON_BODY) ||
            (has(props.sinkConfig, KEY_JSON_BODY) && !props.sinkConfig[KEY_JSON_BODY])) &&
          !!jsonParams
        ) {
          newConfig[KEY_JSON_BODY] = jsonParams;
        }

        if (
          (!has(props.sinkConfig, KEY_FIELDNAME) ||
            (has(props.sinkConfig, KEY_FIELDNAME) && !props.sinkConfig[KEY_FIELDNAME])) &&
          !!payloadFieldName
        ) {
          newConfig[KEY_FIELDNAME] = payloadFieldName;
        }
      }
      props.onSinkConfigChanged({
        ...props.sinkConfig,
        ...newConfig,
      });
    }
  }, []);

  // change handlers
  const _handleGroupingAlogChange = (algo) => {
    // Updating State Value
    updateGrouingAlgo(algo);

    let updatedSinkConfig = {
      'ui.batch_algo': algo.value, // saving extra key to restore
    };

    // Array Case
    if (algo.value === Constants.GROUPING_ALGO[0].value) {
      updatedSinkConfig = {
        ...updatedSinkConfig,
        'body.transform.function': 'messages',
      };
    } else if (algo.value === Constants.GROUPING_ALGO[1].value) {
      // Property inside json case
      updatedSinkConfig = {
        ...updatedSinkConfig,
        'body.transform.function': 'Map("data" -> messages)', // generating scalaformat and storing into its key
        [KEY_JSON_BODY]: '{}', // saving extra key to restore
        [KEY_FIELDNAME]: 'data',
      };
    } else if (algo.value === Constants.GROUPING_ALGO[2].value) {
      // Scala function case
      updatedSinkConfig = {
        ...updatedSinkConfig,
        'body.transform.function': 'messages.map(m => m)',
      };
    }

    // Updating Sink Config
    props.onSinkConfigChanged({
      ...omit(props.sinkConfig, [KEY_JSON_BODY]),
      ...updatedSinkConfig,
    });
  };

  const _handlePayloadFieldChange = (fieldName) => {
    props.onSinkConfigChanged({
      ...props.sinkConfig,
      'body.transform.function': _generateBodyTransformVal(jsonParams, fieldName), // generating scalaformat and storing into its key
      [KEY_FIELDNAME]: fieldName,
    });
  };

  const _handleJSONParamsChange = (jsonBody) => {
    props.onSinkConfigChanged({
      ...props.sinkConfig,
      'body.transform.function': _generateBodyTransformVal(jsonBody, fieldName), // generating scalaformat and storing into its key
      [KEY_JSON_BODY]: jsonBody,
    });
  };

  const _generateBodyTransformVal = (jsonBody, payloadFieldName) => {
    let jsonObj = JSONUtils.isJSON(jsonBody) ? JSONUtils.parseJson(jsonBody) : {};
    const scalaFormatStr = convertJSONtoScala(jsonObj, payloadFieldName);
    return scalaFormatStr;
  };
  /**
   * Render UI function
   * @return React.Component
   */

  return (
    <>
      <div className="input-row ">
        <div className="check-box-holder batch-mode">
          <StyledFormControlLabel
            label="Would you like to batch your records together?"
            className="sink-config sink-config-batch.mode "
            control={
              <StyledCheckbox
                checked={batchMode}
                onChange={(event, isChecked) => {
                  if (isChecked) {
                    props.onSinkConfigChanged({
                      ...props.sinkConfig,
                      'batch.mode': isChecked,
                      'batch.size': batchSize,
                      'body.transform.function': bodyTransFn,
                      'ui.batch_algo': groupingAlgo.value,
                    });
                  } else {
                    props.onSinkConfigChanged(
                      omit(props.sinkConfig, [
                        'batch.mode',
                        'batch.size',
                        'body.transform.function',
                        'ui.batch_algo',
                        ...[omitUiKeys],
                      ]),
                    );
                  }
                }}
              />
            }
          />
        </div>
      </div>

      {batchMode ? (
        <div className="input-row">
          <Stack width="100%" direction="row" alignItems="center" gap={1}>
            <div className="input-holder ">
              <label className="sink-config sink-config-batch.size">Batch Size</label>
              <div>
                <DebouncedText
                  className="sink-config sink-config-batch.size"
                  id=""
                  placeholder="Batch Size"
                  value={batchSize}
                  debounceTimeMs={400}
                  min={0}
                  rootStyle={{ height: '38px', backgroundColor: '#fff' }}
                  type="number"
                  onChange={(data) => {
                    props.onSinkConfigChanged({
                      ...props.sinkConfig,
                      'batch.size': data,
                    });
                  }}
                />
              </div>
            </div>

            {/* Grouping Algo */}
            <div className="input-holder">
              <label className="sink-config sink-config-grouping.algo">Grouping Algorithm</label>
              <UnsortedSelect
                value={groupingAlgo}
                options={Constants.GROUPING_ALGO}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                isSearchable={true}
                backspaceRemovesValue={false}
                styles={redesignStyles}
                onChange={_handleGroupingAlogChange}
              />
            </div>
          </Stack>

          {/* Grouping Algo ALGO_JSON */}
          {groupingAlgo && groupingAlgo.value == Constants.GROUPING_ALGO[1].value && (
            <div className="input-holder">
              <label className="sink-config">Payload Field Name</label>
              <div>
                <DebouncedText
                  className="sink-config"
                  placeholder="data"
                  value={fieldName}
                  debounceTimeMs={400}
                  rootStyle={{
                    height: '38px',
                    backgroundColor: '#fff',
                  }}
                  onChange={_handlePayloadFieldChange}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Only if Grouping Algo Type Object */}
      {batchMode && groupingAlgo && groupingAlgo.value == Constants.GROUPING_ALGO[1].value && (
        <div className="input-row">
          <div className="input-holder ">
            <label className="sink-config">Other Props</label>
            <div>
              <LoadableMonacoEditor
                language={'json'}
                value={jsonParams}
                theme="dark"
                onChange={debounce(_handleJSONParamsChange, 400)}
                height={200}
                options={{
                  minimap: { enabled: false },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Only If Grouping Algo Type ALGO_Code */}
      {batchMode && groupingAlgo && groupingAlgo.value == Constants.GROUPING_ALGO[2].value && (
        <div className="input-row">
          <div className="batch-mode">
            <LoadableMonacoEditor
              language={'scala'}
              value={bodyTransFn}
              theme="dark"
              onChange={debounce((code) => {
                props.onSinkConfigChanged({
                  ...omit(props.sinkConfig, omitUiKeys),
                  'body.transform.function': code,
                });
              }, 200)}
              height={200}
              options={{
                minimap: { enabled: false },
              }}
              className="code-editor"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BatchFields;
